/**
 * Bind a simple audio settings panel to an AudioEngine.
 * Expects elements with ids under `root` (see Proclaim index.html).
 */

import type { AudioEngine } from "./types";
import type { AudioSettings } from "./settings";

export type AudioSettingsPanelHandle = {
  /** Push engine settings into the form */
  syncFromEngine: () => void;
  dispose: () => void;
};

function el<T extends HTMLElement>(root: ParentNode, id: string): T | null {
  return root.querySelector(`#${id}`) as T | null;
}

/**
 * Wire sliders/checkboxes. Returns handle for sync after external changes.
 */
export function bindAudioSettingsPanel(
  root: ParentNode,
  engine: AudioEngine,
  opts?: {
    /** Called after user changes (e.g. unlock audio) */
    onUserChange?: () => void;
  },
): AudioSettingsPanelHandle {
  const panel = el<HTMLElement>(root, "audio-panel");
  const toggleBtn = el<HTMLButtonElement>(root, "btn-audio");
  const master = el<HTMLInputElement>(root, "audio-master");
  const sfx = el<HTMLInputElement>(root, "audio-sfx");
  const state = el<HTMLInputElement>(root, "audio-state");
  const music = el<HTMLInputElement>(root, "audio-music");
  const voice = el<HTMLInputElement>(root, "audio-voice");
  const voiceEnabled = el<HTMLInputElement>(root, "audio-voice-enabled");
  const lieVo = el<HTMLInputElement>(root, "audio-lie-vo");
  const lieVoEnabled = el<HTMLInputElement>(root, "audio-lie-vo-enabled");
  const mute = el<HTMLInputElement>(root, "audio-mute");
  const minimal = el<HTMLInputElement>(root, "audio-minimal");

  const listeners: Array<() => void> = [];

  function on(
    node: HTMLElement | null,
    type: string,
    fn: (e: Event) => void,
  ): void {
    if (!node) return;
    node.addEventListener(type, fn);
    listeners.push(() => node.removeEventListener(type, fn));
  }

  function syncFromEngine(): void {
    const s = engine.getSettings();
    if (master) master.value = String(Math.round(s.master * 100));
    if (sfx) sfx.value = String(Math.round(s.sfx * 100));
    if (state) state.value = String(Math.round(s.state * 100));
    if (music) music.value = String(Math.round(s.music * 100));
    if (voice) voice.value = String(Math.round(s.voice * 100));
    if (voiceEnabled) voiceEnabled.checked = s.voiceEnabled;
    if (lieVo) lieVo.value = String(Math.round(s.lieVo * 100));
    if (lieVoEnabled) lieVoEnabled.checked = s.lieVoEnabled;
    if (mute) mute.checked = s.muted;
    if (minimal) minimal.checked = s.minimal;
  }

  function applyPartial(partial: Partial<AudioSettings>): void {
    engine.applySettings(partial);
    opts?.onUserChange?.();
  }

  function pct(input: HTMLInputElement | null): number {
    if (!input) return 0;
    return Math.max(0, Math.min(1, Number(input.value) / 100));
  }

  on(toggleBtn, "click", () => {
    if (!panel) return;
    const open = panel.classList.toggle("hidden") === false;
    toggleBtn?.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) syncFromEngine();
  });

  on(master, "input", () => applyPartial({ master: pct(master) }));
  on(sfx, "input", () => applyPartial({ sfx: pct(sfx) }));
  on(state, "input", () => applyPartial({ state: pct(state) }));
  on(music, "input", () => {
    // music slider also scales ambient for a simple "bed" control
    const v = pct(music);
    applyPartial({ music: v, ambient: v * 0.9 });
  });
  on(voice, "input", () => applyPartial({ voice: pct(voice) }));
  on(voiceEnabled, "change", () =>
    applyPartial({ voiceEnabled: !!voiceEnabled?.checked }),
  );
  on(lieVo, "input", () => applyPartial({ lieVo: pct(lieVo) }));
  on(lieVoEnabled, "change", () =>
    applyPartial({ lieVoEnabled: !!lieVoEnabled?.checked }),
  );
  on(mute, "change", () => applyPartial({ muted: !!mute?.checked }));
  on(minimal, "change", () => applyPartial({ minimal: !!minimal?.checked }));

  // Click outside to close
  const onDoc = (e: MouseEvent) => {
    if (!panel || panel.classList.contains("hidden")) return;
    const t = e.target as Node;
    if (panel.contains(t) || toggleBtn?.contains(t)) return;
    panel.classList.add("hidden");
    toggleBtn?.setAttribute("aria-expanded", "false");
  };
  document.addEventListener("pointerdown", onDoc, true);
  listeners.push(() => document.removeEventListener("pointerdown", onDoc, true));

  syncFromEngine();

  return {
    syncFromEngine,
    dispose() {
      for (const off of listeners) off();
    },
  };
}
